import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback
} from 'react'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import { Stack } from '@jarvis/web-stratus-client'
import { DirectionContext } from '@veneer/core/dist/scripts/direction'
import {
  EMPTY_FUNCTION,
  FEATURE_FLAG_KEYS,
  HPX_MIN_TABLET_WIDTH,
  RESULT,
  SPAN_PREFIX_RESULT,
  SPAN_WORKFLOW_NAME
} from 'src/config/constants'
import {
  getCurrentServiceId,
  Logger,
  replaceAppBranding,
  replacePortalBranding
} from 'src/utils/helpers'
import useDeviceHttpProxy from 'src/hooks/useDeviceHttpProxy'
import {
  UiTheme,
  useMultiUiBrandContext
} from '@jarvis/react-setup-and-onboarding-addons'
import { FeatureFlagContext } from 'src/store/FeatureFlagContext'

export const ConfigContext = createContext({
  authProvider: null,
  sessionContext: null,
  localization: null,
  stack: Stack.pie,
  authStack: Stack.pie,
  tenantHandler: null,
  shellLanguage: null,
  shellCountry: null,
  getText: EMPTY_FUNCTION,
  getTextTree: EMPTY_FUNCTION,
  closeServiceInstance: EMPTY_FUNCTION,
  sessionId: null,
  sendDHPRequest: null,
  publishCdmEvent: EMPTY_FUNCTION,
  publishRumEvent: EMPTY_FUNCTION,
  isMobile: false,
  isHpx: false,
  isRTL: false,
  direction: 'ltr',
  xCorrelationId: null,
  isSmallville: false,
  isSmbSmallville: false
})

const DEFAULT_COUNTRY = 'us'
const DEFAULT_LANGUAGE = 'en'

const ConfigProvider = (props) => {
  const {
    authProvider = null,
    analytics = null,
    store = null,
    localization = {
      country: DEFAULT_COUNTRY,
      language: DEFAULT_LANGUAGE,
      enabled: false
    },
    serviceRouting = null,
    monitoring = null,
    tenantHandler = null,
    app = null,
    events = null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    t = (key, options) => key
  } = props
  const { uiBrand } = useMultiUiBrandContext()
  const { flags } = useContext(FeatureFlagContext)
  const { sendDHPRequest } = useDeviceHttpProxy()
  const [sessionId, setSessionId] = useState(null)
  const country = localization.country.toUpperCase()
  const language = localization.language
  const publishCdmEvent = analytics?.publishCdmEvents || EMPTY_FUNCTION
  const sessionContext = store?.state?.onboarding?.sessionContext
  const { isRTL } = useContext(DirectionContext)
  const startSpan = monitoring?.startSpan || EMPTY_FUNCTION
  const isSmallville = sessionContext?.onboardingContext?.entry === 'earlyOobe'
  const xCorrelationId = sessionContext?.xCorrelationId
  const isSmbSmallville =
    isSmallville && sessionContext?.onboardingContext?.experience === 'smb'
  const isHpx = uiBrand === UiTheme.hpx
  const isOsTypeMobile = ['ios', 'android'].includes(
    sessionContext?.app?.osType
  )
  const isMobile =
    isOsTypeMobile && (!isHpx || window.innerWidth < HPX_MIN_TABLET_WIDTH)
  const stack = app?.getPortalStack() || Stack.pie
  const authStack = app?.getAuthStack() || Stack.pie
  const triggerEvent = events?.triggerEvent || EMPTY_FUNCTION

  const publishRumEvent = useCallback(
    async (name, attributes = {}) => {
      const span = startSpan(name, attributes)
      await span?.end?.()
    },
    [startSpan]
  )

  const handleTriggerEvent = useCallback(
    (name, data) => {
      data = {
        ...data,
        appSessionId: sessionId,
        serviceId: getCurrentServiceId()
      }
      Logger.log(`calling ${name} triggerEvent with: ${JSON.stringify(data)}`)
      triggerEvent(name, data)
    },
    [triggerEvent, sessionId]
  )

  const closeServiceInstance = useCallback(
    async (result = RESULT.SUCCESS, output = null, errorInfo = null) => {
      const closeServiceInstanceOptions = {
        resultData: { result: result, xCorrelationId: xCorrelationId }
      }
      if (output !== null) {
        closeServiceInstanceOptions.resultData['output'] = output
      }
      if (errorInfo !== null) {
        closeServiceInstanceOptions.resultData['errorInfo'] = errorInfo
      }
      Logger.log('calling closeServiceInstance')
      Logger.log(closeServiceInstanceOptions)
      try {
        await serviceRouting.closeServiceInstance(closeServiceInstanceOptions)
      } catch (error) {
        Logger.error(`call to closeServiceInstance failed - ${error}`)
      }
      publishRumEvent(getCurrentServiceId(), {
        [SPAN_WORKFLOW_NAME]: `${SPAN_PREFIX_RESULT.replace(
          '$serviceId',
          getCurrentServiceId()
        )}${result}`
      })
    },
    [serviceRouting, xCorrelationId, publishRumEvent]
  )

  useEffect(() => {
    async function getAppSessionId() {
      let id = null
      try {
        Logger.log('calling getServiceInstanceLaunchOptions')
        const serviceLaunchOptions =
          await serviceRouting.getServiceInstanceLaunchOptions()
        Logger.log(serviceLaunchOptions)
        const { serviceOptions } = serviceLaunchOptions
        id = serviceOptions.appSessionId
      } catch (e) {
        Logger.warn(`call to getServiceInstanceLaunchOptions failed - ${e}`)
        id = `OWID-${uuidv4()}`
      }
      Logger.log(`appSessionId ${id}`)
      setSessionId(id)
    }
    getAppSessionId().then()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const configState = useMemo(
    () => ({
      authProvider,
      sessionContext,
      localization,
      tenantHandler,
      shellLanguage: language,
      shellCountry: country,
      getText: (key) => {
        const text = replaceAppBranding(t(key), isHpx)
        return flags?.[FEATURE_FLAG_KEYS.portalBrand]
          ? replacePortalBranding(text)
          : text
      },
      getTextTree: (key) => {
        const text = replaceAppBranding(t(key, { returnObjects: true }), isHpx)
        return flags?.[FEATURE_FLAG_KEYS.portalBrand]
          ? replacePortalBranding(text)
          : text
      },
      closeServiceInstance,
      sessionId,
      sendDHPRequest,
      publishCdmEvent,
      publishRumEvent,
      isMobile,
      isHpx,
      isRTL,
      direction: isRTL ? 'rtl' : 'ltr',
      xCorrelationId,
      isSmallville,
      isSmbSmallville,
      authStack,
      stack,
      handleTriggerEvent
    }),
    [
      authProvider,
      sessionContext,
      localization,
      tenantHandler,
      language,
      country,
      closeServiceInstance,
      sessionId,
      sendDHPRequest,
      publishCdmEvent,
      isMobile,
      isHpx,
      isRTL,
      t,
      isSmallville,
      isSmbSmallville,
      xCorrelationId,
      publishRumEvent,
      authStack,
      stack,
      flags,
      handleTriggerEvent
    ]
  )

  return (
    <ConfigContext.Provider value={configState}>
      {props.children}
    </ConfigContext.Provider>
  )
}

ConfigProvider.propTypes = {
  authProvider: PropTypes.object,
  analytics: PropTypes.object,
  store: PropTypes.object,
  localization: PropTypes.object,
  serviceRouting: PropTypes.object,
  monitoring: PropTypes.object,
  tenantHandler: PropTypes.object,
  t: PropTypes.func,
  children: PropTypes.node,
  app: PropTypes.shape({
    getAuthStack: PropTypes.func,
    getPortalStack: PropTypes.func
  }),
  events: PropTypes.shape({
    triggerEvent: PropTypes.func
  })
}

export default ConfigProvider
